// Right inspector for the Freehand workspace: workspace-level settings
// (symmetry, ball opacity, grid) + selected-panel editing tools + colour
// palette. Tools & drawing helpers live in the Left Rail.

import React from 'react';
import { useFreehand, FREEHAND_PALETTE } from '@/lib/freehandStore';
import { COLOR_GROUPS } from '@/lib/store';
import { Hash, RotateCcw, RotateCw } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { symmetryReplicaCount, symmetrySupportsMirror } from '@/lib/freehandGeometry';
import { FreehandScaleAll } from './FreehandScaleAll';

const SYMMETRY_MODES = [
  { id: 'none', label: 'None' },
  { id: 'antipodal', label: 'Antipodal' },
  { id: 'mirrorX', label: 'Mirror X' },
  { id: 'mirrorY', label: 'Mirror Y' },
  { id: 'mirrorZ', label: 'Mirror Z' },
  { id: 'ring', label: 'Ring' },
  { id: 'tetrahedral', label: 'Tetrahedral' },
  { id: 'octahedral', label: 'Octahedral' },
  { id: 'icosahedral', label: 'Icosahedral' },
];

const GRID_TYPES = [
  { id: 'off',    label: 'Off',      hint: 'No grid' },
  { id: 'axes',   label: 'Axes',     hint: 'Three great circles splitting the ball on each axis — perfect for symmetric layouts' },
  { id: 'latlon', label: 'Lat / Lon', hint: 'Great for circular & radial designs' },
  { id: 'hex',    label: 'Hex',      hint: 'Pentagon / hexagon anchor points (Goldberg)' },
  { id: 'tri',    label: 'Tri',      hint: 'Triangular geodesic mesh nodes' },
];

function ColorPicker({ selectedColor, onPick }) {
  const [group, setGroup] = React.useState('Vivid');
  return (
    <div className="space-y-2">
      <div className="flex gap-1">
        {Object.keys(COLOR_GROUPS).map((g) => (
          <button
            key={g}
            onClick={() => setGroup(g)}
            data-testid={`freehand-color-group-${g.toLowerCase()}`}
            className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full transition-colors ${
              group === g ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {g}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-6 gap-1.5">
        {COLOR_GROUPS[group].map((c) => (
          <button
            key={c}
            onClick={() => onPick(c)}
            data-testid={`freehand-color-swatch-${c.replace('#', '')}`}
            className={`w-7 h-7 rounded-lg border transition-transform hover:scale-110 ${
              selectedColor === c ? 'border-slate-900 ring-2 ring-slate-900/20' : 'border-slate-200'
            }`}
            style={{ background: c }}
            title={c}
          />
        ))}
      </div>
    </div>
  );
}

export default function FreehandRightInspector({ selectedId, setSelectedId }) {
  const panels = useFreehand((s) => s.panels);
  const setPanelColor = useFreehand((s) => s.setPanelColor);
  const setAllColors = useFreehand((s) => s.setAllColors);
  const setRainbow = useFreehand((s) => s.setRainbow);
  const deletePanel = useFreehand((s) => s.deletePanel);
  const duplicatePanel = useFreehand((s) => s.duplicatePanel);
  const toggleSmoothVertex = useFreehand((s) => s.toggleSmoothVertex);
  const _pushHistory = useFreehand((s) => s._pushHistory);
  const tool = useFreehand((s) => s.tool);
  const setTool = useFreehand((s) => s.setTool);
  // Workspace settings that used to live in the Left Rail.
  const symmetryMode = useFreehand((s) => s.symmetryMode);
  const setSymmetryMode = useFreehand((s) => s.setSymmetryMode);
  const ringN = useFreehand((s) => s.ringN);
  const setRingN = useFreehand((s) => s.setRingN);
  const symmetryMirror = useFreehand((s) => s.symmetryMirror);
  const setSymmetryMirror = useFreehand((s) => s.setSymmetryMirror);
  const ballOpacity = useFreehand((s) => s.ballOpacity);
  const setBallOpacity = useFreehand((s) => s.setBallOpacity);
  const gridType = useFreehand((s) => s.gridType);
  const setGridType = useFreehand((s) => s.setGridType);
  const gridDensity = useFreehand((s) => s.gridDensity);
  const setGridDensity = useFreehand((s) => s.setGridDensity);
  const gridOpacity = useFreehand((s) => s.gridOpacity);
  const setGridOpacity = useFreehand((s) => s.setGridOpacity);
  const snapToGrid = useFreehand((s) => s.snapToGrid);
  const setSnapToGrid = useFreehand((s) => s.setSnapToGrid);
  const showPanelNumbers = useFreehand((s) => s.showPanelNumbers);
  const setShowPanelNumbers = useFreehand((s) => s.setShowPanelNumbers);
  const rotateAll = useFreehand((s) => s.rotateAll);
  const panelCount = panels.length;

  const selected = panels.find((p) => p.id === selectedId);
  const smoothCount = selected ? (selected.smooth?.length || 0) : 0;
  const allSmooth = selected && smoothCount === selected.points.length;

  const roundAll = () => {
    if (!selected) return;
    _pushHistory();
    const idxs = selected.points.map((_, i) => i);
    idxs.forEach((i) => {
      if (!selected.smooth?.includes(i)) toggleSmoothVertex(selected.id, i);
    });
  };
  const sharpenAll = () => {
    if (!selected || !selected.smooth?.length) return;
    _pushHistory();
    // Clone before mutating — the store toggles remove indices as we go.
    [...selected.smooth].forEach((i) => toggleSmoothVertex(selected.id, i));
  };

  return (
    <div className="w-full h-full flex flex-col text-slate-900" data-testid="freehand-right-inspector">
      <div className="px-4 pt-4 pb-2 border-b border-slate-200">
        <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">Panel</div>
        <div className="text-sm font-semibold">{selected ? 'Edit selected' : 'Palette & tools'}</div>
      </div>

      <div className="p-3 space-y-3 flex-1 overflow-y-auto">
        {/* ----- Workspace: Rotate all panels around ball origin ----- */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
            <RotateCw className="w-3 h-3" /> Rotate all panels
          </div>
          <div className="text-[11px] text-slate-500 leading-snug">
            Nudge every panel around the ball's origin to re-align a
            composition to the XYZ axes. Undoable.
          </div>
          {[
            { axis: 'x', label: 'X', color: 'text-red-500' },
            { axis: 'y', label: 'Y', color: 'text-emerald-600' },
            { axis: 'z', label: 'Z', color: 'text-blue-500' },
          ].map(({ axis, label, color }) => (
            <div key={axis} className="flex items-center gap-1.5" data-testid={`freehand-rotate-row-${axis}`}>
              <div className={`w-4 text-xs font-semibold ${color}`}>{label}</div>
              {[-15, -1, 1, 15].map((deg) => (
                <button
                  key={deg}
                  disabled={panelCount === 0}
                  onClick={() => rotateAll(axis, deg)}
                  data-testid={`freehand-rotate-${axis}-${deg}`}
                  className="flex-1 flex items-center justify-center gap-0.5 h-7 rounded-lg border border-slate-200 bg-white text-[11px] font-mono text-slate-700 hover:border-slate-900 hover:bg-slate-50 disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white transition-colors"
                >
                  {deg < 0 ? <RotateCcw className="w-3 h-3" /> : <RotateCw className="w-3 h-3" />}
                  {Math.abs(deg)}°
                </button>
              ))}
            </div>
          ))}
        </div>

        <FreehandScaleAll panelCount={panelCount} />

        {/* ----- Workspace: Symmetry ----- */}
        <div className="space-y-2 pt-3 border-t border-slate-200">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Symmetry</div>
            <div
              className="text-[10px] font-mono text-slate-500"
              data-testid="freehand-sym-replica-count"
            >
              {symmetryReplicaCount(symmetryMode, ringN, symmetryMirror)} {symmetryReplicaCount(symmetryMode, ringN, symmetryMirror) === 1 ? 'copy' : 'copies'}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {SYMMETRY_MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => {
                  setSymmetryMode(m.id);
                  // Hide-and-reset: mirror has no meaning for modes that
                  // don't expose the switch, so clear it to avoid stale
                  // state the next time a supporting mode is picked.
                  if (!symmetrySupportsMirror(m.id)) setSymmetryMirror(false);
                }}
                data-testid={`freehand-sym-${m.id}`}
                className={`rounded-lg text-xs font-medium py-1.5 border transition-colors ${
                  symmetryMode === m.id
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          {symmetrySupportsMirror(symmetryMode) && (
            <div
              className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 border border-slate-200 px-2.5 py-1.5"
              data-testid="freehand-sym-mirror-row"
            >
              <div className="min-w-0">
                <div className="text-[11px] font-medium text-slate-800">
                  {symmetryMode === 'ring' ? 'Flip alternating copies' : 'Include mirror reflections'}
                </div>
                <div className="text-[10px] text-slate-500 leading-snug">
                  {symmetryMode === 'ring'
                    ? 'Every other copy is flipped top↔bottom around the ring — classic alternating panel pattern.'
                    : "Doubles replicas by reflecting each copy across the mode's mirror planes."}
                </div>
              </div>
              <Switch
                checked={symmetryMirror}
                onCheckedChange={(v) => setSymmetryMirror(v)}
                data-testid="freehand-sym-mirror-toggle"
              />
            </div>
          )}
          {symmetryMode === 'ring' && (
            <label className="text-[11px] text-slate-500 flex items-center gap-2">
              <span>Fold</span>
              <input
                type="range"
                min={2}
                max={12}
                step={1}
                value={ringN}
                onChange={(e) => setRingN(Number(e.target.value))}
                className="flex-1"
                data-testid="freehand-ring-n"
              />
              <span className="font-mono w-6 text-right">{ringN}</span>
            </label>
          )}
        </div>

        {/* ----- Workspace: Ball opacity ----- */}
        <div className="pt-3 border-t border-slate-200 space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Ball opacity</div>
          <div className="flex items-center gap-2">
            <input
              type="range"
              min={0.1}
              max={1}
              step={0.05}
              value={ballOpacity}
              onChange={(e) => setBallOpacity(Number(e.target.value))}
              className="flex-1"
              data-testid="freehand-ball-opacity"
            />
            <span className="font-mono text-xs text-slate-600 w-9 text-right">{Math.round(ballOpacity * 100)}%</span>
          </div>
        </div>

        {/* ----- Workspace: Panel numbers toggle ----- */}
        <div className="pt-3 border-t border-slate-200">
          <button
            type="button"
            onClick={() => setShowPanelNumbers(!showPanelNumbers)}
            role="switch"
            aria-checked={showPanelNumbers}
            data-testid="freehand-toggle-panel-numbers"
            className="w-full flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-50 transition-colors"
          >
            <Hash className="w-4 h-4 text-slate-500 shrink-0" />
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-slate-900">Show panel numbers</div>
              <div className="text-[11px] text-slate-500 leading-snug">
                Floats each panel's export number on the ball so you can match a printed sheet to the 3D model.
              </div>
            </div>
            <span
              className={`inline-flex w-9 h-5 shrink-0 rounded-full transition-colors ${showPanelNumbers ? 'bg-[#007AFF]' : 'bg-slate-300'}`}
            >
              <span
                className={`w-4 h-4 mt-0.5 rounded-full bg-white shadow transition-transform ${showPanelNumbers ? 'translate-x-4' : 'translate-x-0.5'}`}
              />
            </span>
          </button>
        </div>

        {/* ----- Workspace: Grid settings ----- */}
        <div className="pt-3 border-t border-slate-200 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Grid</div>
          <div className="grid grid-cols-2 gap-1.5">
            {GRID_TYPES.map((g) => (
              <button
                key={g.id}
                onClick={() => setGridType(g.id)}
                data-testid={`freehand-grid-type-${g.id}`}
                title={g.hint}
                className={`rounded-lg text-xs font-medium py-1.5 px-1 border transition-colors ${
                  gridType === g.id
                    ? 'border-pink-500 bg-pink-500 text-white'
                    : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                {g.label}
              </button>
            ))}
          </div>
          {gridType !== 'off' && (
            <div className="text-[11px] text-slate-500 leading-snug bg-pink-50 border border-pink-100 rounded-lg p-2">
              {GRID_TYPES.find((g) => g.id === gridType)?.hint}
            </div>
          )}
          {gridType !== 'off' && (
            <>
              <button
                onClick={() => setSnapToGrid(!snapToGrid)}
                data-testid="freehand-grid-snap-checkbox"
                className={`w-full rounded-xl px-3 py-2 border transition-colors flex items-center gap-2.5 ${
                  snapToGrid
                    ? 'border-pink-500 bg-pink-500 text-white shadow-sm'
                    : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'
                }`}
              >
                <span className={`w-8 h-4 rounded-full relative transition-colors ${snapToGrid ? 'bg-white/30' : 'bg-slate-200'}`}>
                  <span className={`absolute top-0.5 w-3 h-3 rounded-full transition-all ${snapToGrid ? 'left-4 bg-white' : 'left-0.5 bg-white'}`} />
                </span>
                <div className="flex-1 text-left">
                  <div className="text-sm font-semibold">Snap to grid</div>
                  <div className={`text-[11px] ${snapToGrid ? 'text-pink-100' : 'text-slate-500'} leading-snug`}>
                    {snapToGrid ? 'Points lock onto grid intersections' : 'Grid is visible but points draw freely'}
                  </div>
                </div>
              </button>
              <label className="text-[11px] text-slate-500 flex items-center gap-2">
                <span className="w-14">Density</span>
                <input
                  type="range"
                  min={gridType === 'latlon' ? 2 : 1}
                  max={gridType === 'latlon' ? 12 : gridType === 'axes' ? 8 : 4}
                  step={1}
                  value={gridDensity}
                  onChange={(e) => setGridDensity(Number(e.target.value))}
                  className="flex-1"
                  data-testid="freehand-grid-density"
                />
                <span className="font-mono w-6 text-right">{gridDensity}</span>
              </label>
              <label className="text-[11px] text-slate-500 flex items-center gap-2">
                <span className="w-14">Opacity</span>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={gridOpacity}
                  onChange={(e) => setGridOpacity(Number(e.target.value))}
                  className="flex-1"
                  data-testid="freehand-grid-opacity"
                />
                <span className="font-mono w-9 text-right">{Math.round(gridOpacity * 100)}%</span>
              </label>
            </>
          )}
        </div>

        <div className="pt-3 border-t border-slate-200" />

        {selected ? (
          <>
            <div className="text-[11px] text-slate-500 flex items-center justify-between">
              <span>Selected <span className="font-mono text-slate-800">{selected.id}</span> · {selected.points.length} pts</span>
              <button
                onClick={() => { deletePanel(selected.id); setSelectedId(null); }}
                data-testid="freehand-delete-selected"
                className="text-red-600 hover:underline"
              >
                Delete
              </button>
            </div>
            {tool === 'edit' ? (
              <div className="text-[11px] text-slate-500 leading-snug bg-slate-50 border border-slate-200 rounded-xl p-2.5">
                <span className="font-semibold text-slate-700">Editing:</span> drag any handle to move a corner ·
                <span className="whitespace-nowrap"> double-click</span> a handle to round or sharpen it.
                {selected.group && ' Edits mirror across sibling panels in the same symmetry group.'}
              </div>
            ) : (
              <button
                onClick={() => setTool('edit')}
                data-testid="freehand-switch-to-edit"
                className="w-full rounded-lg text-xs font-medium py-2 bg-slate-900 text-white hover:bg-slate-800 flex items-center justify-center gap-1.5"
              >
                Switch to Edit tool to reshape
              </button>
            )}
            <div className="grid grid-cols-2 gap-1.5">
              <button
                onClick={roundAll}
                disabled={allSmooth}
                data-testid="freehand-round-all"
                className="rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300 disabled:opacity-40"
              >
                Round all corners
              </button>
              <button
                onClick={sharpenAll}
                disabled={!smoothCount}
                data-testid="freehand-sharpen-all"
                className="rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300 disabled:opacity-40"
              >
                Sharpen all corners
              </button>
            </div>
            <div className="text-[11px] font-mono text-slate-500 text-right">{smoothCount}/{selected.points.length} rounded</div>
            <div className="pt-2 border-t border-slate-200 space-y-1.5">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Duplicate</div>
              <div className="grid grid-cols-3 gap-1.5">
                {[
                  { id: 'copy',      label: 'Copy',       hint: 'Rotate 30° yaw so the twin sits next to the original' },
                  { id: 'antipodal', label: 'Antipodal',  hint: 'Through-centre inversion — opposite side of the ball' },
                  { id: 'mirrorX',   label: 'Mirror X',   hint: 'Reflect across the vertical (YZ) plane' },
                  { id: 'mirrorY',   label: 'Mirror Y',   hint: 'Reflect across the horizontal (XZ) plane' },
                  { id: 'mirrorZ',   label: 'Mirror Z',   hint: 'Reflect across the front-back (XY) plane' },
                ].map(({ id, label, hint }) => (
                  <button
                    key={id}
                    onClick={() => {
                      duplicatePanel(selected.id, id);
                      // Auto-select the freshly created panel so the user can
                      // immediately manipulate/edit it.
                      setTimeout(() => {
                        const st = useFreehand.getState();
                        const last = st.panels[st.panels.length - 1];
                        if (last) setSelectedId(last.id);
                      }, 0);
                    }}
                    data-testid={`freehand-dup-${id}`}
                    title={hint}
                    className="rounded-lg text-xs font-medium py-1.5 px-1 bg-white border border-slate-200 hover:border-slate-900 hover:bg-slate-50 text-slate-700 transition-colors"
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-slate-500 leading-snug bg-slate-50 border border-slate-200 rounded-lg p-2">
                The copy is a fresh independent panel — use the manipulate tool (double-click) to fine-tune its position.
              </div>
            </div>
            <ColorPicker
              selectedColor={selected.color}
              onPick={(c) => setPanelColor(selected.id, c)}
            />
          </>
        ) : (
          <div className="text-[11px] text-slate-500 leading-snug bg-slate-50 border border-slate-200 rounded-xl p-3">
            Click a committed panel on the ball to edit its points, round corners, or change its colour. Or use the presets below to recolour every panel at once.
          </div>
        )}

        <div className="pt-3 border-t border-slate-200 space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Quick recolour</div>
          <button
            onClick={setRainbow}
            data-testid="freehand-recolor-rainbow"
            className="w-full rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300 flex items-center justify-center gap-1.5"
          >
            <span className="flex gap-0.5">
              {FREEHAND_PALETTE.slice(0, 6).map((c) => (
                <span key={c} className="w-2 h-2 rounded-full" style={{ background: c }} />
              ))}
            </span>
            Rainbow
          </button>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => setAllColors('#111827', '#F9FAFB')}
              data-testid="freehand-recolor-classic"
              className="rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300"
            >
              B/W
            </button>
            <button
              onClick={() => setAllColors('#007AFF', '#F9FAFB')}
              data-testid="freehand-recolor-blue"
              className="rounded-lg text-xs font-medium py-2 bg-white border border-slate-200 hover:border-slate-300"
            >
              Blue/White
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
